import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { createPortal } from 'react-dom';
import { useLanguage } from './Header';
import { getTranslation, Language } from '../translations';
import { supabase } from '../config/supabase';
import { getPortalRoot } from '../utils/portalRoot';

// ── Animations ──
const overlayIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const overlayOut = keyframes`from { opacity: 1; } to { opacity: 0; }`;
const dialogIn = keyframes`from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); }`;
const dialogOut = keyframes`from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.95); }`;
const sheetIn = keyframes`from { transform: translateY(100%); } to { transform: translateY(0); }`;
const sheetOut = keyframes`from { transform: translateY(0); } to { transform: translateY(100%); }`;
const spin = keyframes`100% { transform: rotate(360deg); }`;

// ── Styled Components (matching CartContactModal split-pane style) ──
const Overlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$closing',
})<{ $closing?: boolean }>`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  will-change: opacity;
  animation: ${({ $closing }) => $closing ? overlayOut : overlayIn} 0.3s ease forwards;

  @media (max-width: 640px) {
    align-items: flex-end;
  }
`;

const Dialog = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$closing',
})<{ $closing?: boolean }>`
  background: #f8fafc;
  border-radius: 20px;
  max-width: 720px;
  width: 95%;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  will-change: transform, opacity;
  animation: ${({ $closing }) => $closing ? dialogOut : dialogIn} 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;

  .dark & {
    background: #0f1216;
    border: 1px solid #2e3441;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.45);
  }

  @media (max-width: 640px) {
    width: 100%;
    max-width: none;
    border-radius: 20px 20px 0 0;
    flex-direction: column;
    max-height: 92vh;
    animation: ${({ $closing }) => $closing ? sheetOut : sheetIn} 0.35s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
`;

const BrandPane = styled.div`
  width: 38%;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  padding: 1.75rem;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  * { font-family: inherit; }

  @media (max-width: 640px) {
    width: 100%;
    padding: 1.25rem 1.5rem 1rem;
  }
`;

const FormPane = styled.div`
  width: 62%;
  padding: 1.75rem;
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  * { font-family: inherit; }

  @media (max-width: 640px) {
    width: 100%;
    padding: 1.25rem 1.5rem 2rem;
  }
`;

const CloseIconButton = styled.button`
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #64748b;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #475569;
    transform: scale(1.05);
  }
  &:hover svg { transform: rotate(90deg); }
  svg { transition: transform 0.2s ease; }

  .dark & {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.12);
    color: #94a3b8;
    &:hover { background: rgba(255, 255, 255, 0.15); color: #cbd5e1; }
  }
`;

const CompactUploadZone = styled.div`
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  background: white;
  position: relative;
  transition: all 0.2s;

  &:hover { border-color: #94a3b8; background: #f8fafc; }
  .dark & { background: #151921; border-color: #334155; &:hover { background: #1e293b; } }

  input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
`;

// ── Helpers ──
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '0.85rem',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  background: '#ffffff',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: '600',
  color: '#334155',
  marginBottom: '0.35rem',
};

const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  e.target.style.borderColor = '#4b5563';
  e.target.style.outline = 'none';
  e.target.style.boxShadow = '0 0 0 3px rgba(75, 85, 99, 0.1)';
};

const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  e.target.style.borderColor = '#e2e8f0';
  e.target.style.boxShadow = 'none';
};

// Contextual placeholders per device type (example = translated "e.g." label)
const getModelPlaceholder = (deviceType: string | undefined, example: string): string => {
  const type = (deviceType || '').toLowerCase();
  if (type.includes('samsung')) return `${example} Galaxy S24`;
  if (type.includes('iphone')) return `${example} iPhone 15 Pro`;
  if (type.includes('ipad')) return `${example} iPad Air M2`;
  if (type.includes('mac')) return `${example} MacBook Pro 14"`;
  if (type.includes('watch')) return `${example} Apple Watch Series 9`;
  if (type.includes('huawei')) return `${example} P60 Pro`;
  if (type.includes('xiaomi')) return `${example} 14 Ultra`;
  if (type.includes('google') || type.includes('pixel')) return `${example} Pixel 8 Pro`;
  if (type.includes('oneplus')) return `${example} 12 Pro`;
  return `${example} Galaxy S24`;
};

const getBrandPlaceholder = (deviceType: string | undefined, example: string): string => {
  const type = (deviceType || '').toLowerCase();
  if (type.includes('samsung')) return 'Samsung';
  if (type.includes('iphone') || type.includes('ipad') || type.includes('mac') || type.includes('watch')) return 'Apple';
  if (type.includes('huawei')) return 'Huawei';
  if (type.includes('xiaomi')) return 'Xiaomi';
  if (type.includes('google') || type.includes('pixel')) return 'Google';
  if (type.includes('oneplus')) return 'OnePlus';
  return `${example} Samsung`;
};

// ── Component ──
interface CustomDeviceRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  deviceType?: string;
}

const CustomDeviceRequestModal: React.FC<CustomDeviceRequestModalProps> = ({ isOpen, onClose, deviceType }) => {
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language as Language, key);
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    problem: '',
    contactName: '',
    contactEmail: '',
    contactPhone: ''
  });
  const [fileCount, setFileCount] = useState(0);
  const [files, setFiles] = useState<FileList | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [closing, setClosing] = useState(false);

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 640;

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 300);
  };

  const uploadFileToStorage = async (file: File): Promise<string | null> => {
    const timestamp = Date.now();
    // Unguessable name: the bucket is public, so the link must not be predictable
    const randomId = crypto.randomUUID();
    const fileExt = file.name.split('.').pop() || 'jpg';
    const fileName = `${timestamp}_${randomId}.${fileExt}`;
    const filePath = `custom-requests/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('custom-requests')
      .upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      return null;
    }

    const { data } = supabase.storage.from('custom-requests').getPublicUrl(filePath);
    return data?.publicUrl || null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const photoUrls: string[] = [];
      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          const url = await uploadFileToStorage(files[i]);
          if (url) photoUrls.push(url);
        }
      }

      const data = new FormData();
      data.append("_subject", `Nieuwe aanvraag: ${formData.brand} ${formData.model}`);
      data.append("_replyto", formData.contactEmail);
      data.append("_template", "table");
      data.append("_captcha", "false");
      data.append("Merk", formData.brand);
      data.append("Model", formData.model);
      data.append("Probleem", formData.problem);
      data.append("Naam", formData.contactName);
      data.append("Email", formData.contactEmail);
      data.append("Telefoon", formData.contactPhone);

      if (photoUrls.length > 0) {
        const formattedPhotos = photoUrls.map((url, i) => `Foto ${i + 1}: ${url}`).join('\n');
        data.append("Foto's", `${photoUrls.length} foto('s) bijgevoegd:\n\n${formattedPhotos}`);
      } else {
        data.append("Foto's", "Geen foto's bijgevoegd");
      }

      const response = await fetch("https://formsubmit.co/ajax/info@phonebros.be", {
        method: "POST",
        body: data
      });

      if (response.ok) {
        setIsSubmitting(false);
        setIsSuccess(true);
        setTimeout(() => {
          setIsSuccess(false);
          handleClose();
          setFormData({ brand: '', model: '', problem: '', contactName: '', contactEmail: '', contactPhone: '' });
          setFileCount(0);
          setFiles(null);
        }, 2500);
      } else {
        throw new Error("Er ging iets mis.");
      }
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
      alert(t('somethingWentWrong'));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  if (!isOpen) return null;

  return createPortal(
    <Overlay $closing={closing} onClick={handleClose}>
      <Dialog $closing={closing} onClick={(e) => e.stopPropagation()}>
        {/* ── Brand Pane (left / top on mobile) ── */}
        <BrandPane>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
              <img src="/images/phonebroslogo.png" alt="Phone Bros" style={{ height: '32px', filter: 'brightness(0) invert(1)' }} />
              <span style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Phone Bros</span>
            </div>
            <h2 style={{ fontSize: isMobile ? '1.15rem' : '1.5rem', fontWeight: 'bold', marginBottom: '0.625rem', lineHeight: 1.25 }}>
              {isSuccess
                ? t('requestSentTitle')
                : (t('proposeDifferentModel') || 'Model niet gevonden?')
              }
            </h2>
            <p style={{ fontSize: isMobile ? '0.8rem' : '0.9rem', opacity: 0.8, lineHeight: 1.5 }}>
              {isSuccess
                ? t('requestSentDescription')
                : (t('proposeDifferentModelDescription') || 'Stel een ander model voor en we nemen contact met je op.')
              }
            </p>
          </div>
          <div style={{ fontSize: '0.75rem', opacity: 0.5 }}>
            &copy; {new Date().getFullYear()} Phone Bros
          </div>
        </BrandPane>

        {/* ── Form Pane (right / bottom on mobile) ── */}
        <FormPane>
          {isSuccess ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                <svg style={{ width: '40px', height: '40px' }} fill="white" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e293b', marginBottom: '0.875rem' }}>
                {t('requestSentTitle')}
              </h3>
              <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.6, marginBottom: '1.25rem', maxWidth: '300px' }}>
                {t('requestSentDescription')}
              </p>
              <div style={{ background: 'rgba(239, 246, 255, 0.6)', padding: '1rem', borderRadius: '8px', border: '1px solid #dbeafe', display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.875rem', color: '#1e3a8a' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><polyline points="12,6 12,12 16,14" />
                </svg>
                {t('windowClosesAutomatically')}
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: '600', color: '#1e293b', margin: 0 }}>
                  {t('proposeDifferentModel') || 'Model voorstellen'}
                </h3>
                <CloseIconButton onClick={handleClose} aria-label={t('close')}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" style={{ width: '1.25rem', height: '1.25rem' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </CloseIconButton>
              </div>

              <p style={{ color: '#475569', marginBottom: '1.25rem', lineHeight: 1.5, fontSize: '0.875rem' }}>
                {t('proposeDifferentModelDescription') || 'Vul de gegevens in en we nemen contact met je op.'}
              </p>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={labelStyle}>{t('brandLabel')} <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="text" name="brand" required value={formData.brand} onChange={handleChange} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} placeholder={getBrandPlaceholder(deviceType, t('forExample'))} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('modelLabel')} <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="text" name="model" required value={formData.model} onChange={handleChange} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} placeholder={getModelPlaceholder(deviceType, t('forExample'))} />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>{t('descriptionLabel')}</label>
                  <textarea
                    name="problem"
                    rows={3}
                    value={formData.problem}
                    onChange={handleChange}
                    style={{ ...inputStyle, resize: 'vertical' as const, minHeight: '70px' }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={t('describeDevicePlaceholder')}
                  />
                </div>

                <CompactUploadZone>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files) {
                        if (e.target.files.length > 5) {
                          alert(t('maxPhotosAlert'));
                          e.target.value = "";
                          setFileCount(0);
                          setFiles(null);
                          return;
                        }
                        setFileCount(e.target.files.length);
                        setFiles(e.target.files);
                      }
                    }}
                  />
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#64748b" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>
                      {fileCount > 0 ? t('photosSelected').replace('{count}', String(fileCount)) : t('addPhotos')}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: '#94a3b8', marginLeft: '0.5rem' }}>{t('photosMaxHint')}</span>
                  </div>
                </CompactUploadZone>

                <div style={{ height: '1px', background: '#e2e8f0', margin: '0.5rem 0' }} />

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={labelStyle}>{t('name')} <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="text" name="contactName" required value={formData.contactName} onChange={handleChange} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} placeholder={t('yourNamePlaceholder')} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('phone')}</label>
                    <input type="tel" name="contactPhone" value={formData.contactPhone} onChange={handleChange} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} placeholder={t('phoneNumber')} />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>{t('email')} <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="email" name="contactEmail" required value={formData.contactEmail} onChange={handleChange} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} placeholder={t('emailPlaceholder')} />
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                  <button
                    type="button"
                    onClick={handleClose}
                    style={{ padding: '0.625rem 1.25rem', borderRadius: '8px', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.2s ease', border: '1px solid #cbd5e1', background: 'white', color: '#334155', fontFamily: 'inherit' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#94a3b8'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      padding: '0.625rem 1.25rem', borderRadius: '8px', fontWeight: '600', fontSize: '0.875rem',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease',
                      border: '1px solid transparent',
                      background: isSubmitting ? '#9ca3af' : '#374151',
                      color: 'white', fontFamily: 'inherit',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                      opacity: isSubmitting ? 0.8 : 1,
                    }}
                    onMouseEnter={(e) => { if (!isSubmitting) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.background = '#4b5563'; } }}
                    onMouseLeave={(e) => { if (!isSubmitting) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.background = '#374151'; } }}
                  >
                    {isSubmitting && (
                      <svg style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} fill="none" viewBox="0 0 24 24">
                        <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    )}
                    <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
                    {isSubmitting ? t('sending') : t('sendRequest')}
                    {!isSubmitting && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </FormPane>
      </Dialog>
    </Overlay>,
    getPortalRoot()
  );
};

export default CustomDeviceRequestModal;
